/**
 * Client Service
 *
 * Handles the tenant/client cookie lifecycle and post-login redirect logic.
 *
 * Flow:
 *  1. access.clinarya.com?client=<base64> → decode → save session cookie
 *  2. After login, read cookie → redirect to {tenant}.clinarya.com
 *  3. If no cookie → navigate to /select-client so the user can choose a tenant
 */
import {ENVIROMENT} from "../config/env.ts";
import {ensureTenantCookieFromQuery} from "./authService.ts";
const TENANT_COOKIE_NAME = 'CLINARYA_SUBDOMAIN';

/**
 * Decode the base64 `client` query-param and persist the tenant as a session cookie.
 * Session cookies (no `expires`) are cleared when the browser session ends,
 * which means they survive OAuth redirects within the same browser session.
 *
 * Expected payload: { "tenant": "cliente1" }
 */
export function saveTenantFromBase64(clientB64: string): void {
  try {
    // atob handles standard base64; btoa/atob don't accept URL-safe chars, so normalise first
    const normalised = clientB64.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalised);
    const data: unknown = JSON.parse(decoded);
    if (
      data !== null &&
      typeof data === 'object' &&
      'tenant' in data &&
      typeof (data as { tenant: unknown }).tenant === 'string'
    ) {
      const tenant = (data as { tenant: string }).tenant.trim();
      if (tenant) {
        // SameSite=Lax allows the cookie to be sent on top-level navigations (e.g. OAuth redirects)
        document.cookie = `${TENANT_COOKIE_NAME}=${encodeURIComponent(tenant)}; path=/; SameSite=Lax`;
      }
    }
  } catch (err) {
    console.error('[clientService] Invalid client parameter – could not decode base64 JSON.', err);
  }
}

/**
 * Read the tenant from the session cookie.
 * Returns `null` if the cookie is absent or empty.
 */
export function getTenantFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const raw of cookies) {
    const [name, ...rest] = raw.trim().split('=');
    if (name === TENANT_COOKIE_NAME) {
      const value = rest.join('='); // handle '=' inside value
      return value ? decodeURIComponent(value) : null;
    }
  }
  if (ENVIROMENT == 'development') {
    return 'local'
  }

  return ensureTenantCookieFromQuery(location.search);
}

/**
 * Persist a plain tenant string directly as a session cookie.
 * Use this when you already have the decoded tenant value (e.g. from user input).
 */
export function saveTenantDirect(tenant: string): void {
  const trimmed = tenant.trim();
  if (trimmed) {
    document.cookie = `${TENANT_COOKIE_NAME}=${encodeURIComponent(trimmed)}; path=/; SameSite=Lax`;
  }
}

/**
 * Redirect to `https://{tenant}.clinarya.com`, replacing the current history
 * entry instead of pushing a new one.
 *
 * `replace` (not `href`) is deliberate: it keeps the page we are leaving out of
 * the browser history. On the Google callback that page is a spinner whose URL
 * used to carry the authorization code, and returning to it via the back button
 * is what caused codes to be replayed. As a side effect, "back" from the tenant
 * no longer returns into a completed login flow, which is the behaviour we want
 * for an auth portal.
 */
export function redirectToTenant(tenant: string): void {
  window.location.replace(`https://${tenant}.clinarya.com`);
}

/**
 * Redirect to the tenant portal after a successful login.
 *
 * Since `CLINARYA_SUBDOMAIN` is now required (enforced by AuthWrapper),
 * the cookie must already be present at this point.  If for some reason
 * it is absent, an error is logged and no navigation is performed.
 */
export function handlePostLoginRedirect(): void {
  const tenant = getTenantFromCookie();
  if (tenant) {
    redirectToTenant(tenant);
  } else {
    console.error(
      '[clientService] handlePostLoginRedirect: CLINARYA_SUBDOMAIN cookie is missing. ' +
      'Access should have been blocked by AuthWrapper.'
    );
  }
}
